/**
 * SymbolHeatmap — the tracked-symbol board: what moved, what traded, how broad.
 *
 * Three rows, because three different questions:
 *   Top Gainers / Top Losers   what moved (by today's % change)
 *   Most Active                where the money went (24h turnover in USD)
 * plus an advance/decline strip that says how broad the move is — and, crucially,
 * over WHAT, because A/D over 188 Delta symbols is not a market-breadth reading
 * and the header says so rather than letting the ratio imply one.
 *
 * FILTERS are driven by the `assetClass` the API now returns, which comes from
 * Delta's own product tag (top_tag 'tradfi' vs 'crypto'), not from a symbol
 * pattern. See backend utils/assetClass.ts for why that distinction matters.
 *
 * COLOUR RULE, learned the hard way (2026-09-03): the bucket tint is composited
 * over the PAGE, so a 50%-alpha teal is LIGHT in light mode and DARK in dark mode
 * — the opposite of a solid brand colour. Painting white on it measured 1.69:1.
 * Every bucket uses `text.primary`, which flips with the theme and clears
 * 6.1–12.9:1 on all of them. The tint already carries the signal; colouring the
 * text too was redundant and is what broke it. Do not reintroduce it.
 */
import React, { useMemo, useState } from 'react';
import { CH_TYPE } from '../../styles/appUi';
import { Card, CardHeader, CardContent, Box, Typography, Tooltip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppTheme } from '../../hooks/useAppTheme';
import EmptyState from '../common/EmptyState';
import { useTabs } from '../../contexts/TabsContext';

/** Mirrors the backend AssetClass union (backend/src/utils/assetClass.ts). */
export type AssetClass =
  | 'CRYPTO' | 'RWA_EQUITY' | 'RWA_METAL'
  | 'EQUITY_IN' | 'FNO_IN' | 'MCX' | 'CDS' | 'NCO' | 'GLOBAL' | 'UNKNOWN';

export interface HeatmapCell {
  symbol: string;
  exchange?: string;
  pctChange: number | null;
  spotPrice?: number | null;
  assetClass?: AssetClass;
  /** 24h turnover in USD. Delta only — Zerodha's bulk OHLC carries no turnover,
   *  so NSE/MCX/CDS rows are absent here rather than zero. */
  turnoverUsd?: number | null;
}

/** One stored advance/decline sample, as served by /charts/breadth-history. */
export interface BreadthPoint {
  t: number;
  groups: Record<string, { a: number; d: number; u: number; n: number }>;
}

interface Props {
  cells: HeatmapCell[];
  title?: string;
  subheader?: string;
  /** R115 Stage 7 — render the ranked rows rather than one full grid. */
  splitTopBottom?: boolean;
  /** Intraday A/D history for the sparkline. Empty until the first samples land. */
  breadth?: BreadthPoint[];
}

const bucketColor = (pct: number | null, _C: any, isDark = false): { bg: string; text: string } => {
  if (pct == null || !Number.isFinite(pct)) return { bg: 'rgba(128,128,128,0.10)', text: 'text.disabled' };
  if (pct <= -3) return { bg: 'rgba(239,83,80,0.55)',  text: 'text.primary' };
  if (pct < 0) return isDark
    ? { bg: 'rgba(239,83,80,0.30)',  text: 'text.primary' }
    : { bg: 'rgba(239,83,80,0.22)',  text: 'text.primary' };
  if (pct >=  3) return { bg: 'rgba(38,166,154,0.50)', text: 'text.primary' };
  if (pct > 0) return isDark
    ? { bg: 'rgba(38,166,154,0.28)', text: 'text.primary' }
    : { bg: 'rgba(38,166,154,0.20)', text: 'text.primary' };
  return { bg: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(128,128,128,0.06)', text: 'inherit' };
};

/** Groups the user asked for. `ALL` is kept as the default so the board still
 *  opens on everything the account touches. */
type GroupFilter = 'ALL' | 'CRYPTO_COIN' | 'CRYPTO_RWA' | 'NSE_BSE' | 'CDS' | 'MCX';
const FILTER_KEY = 'dash.heatmap.filter.v2';   // v2: the group set changed

const GROUP_LABEL: Record<GroupFilter, string> = {
  ALL: 'All',
  CRYPTO_COIN: 'Crypto Coin',
  CRYPTO_RWA: 'Crypto RWA',
  NSE_BSE: 'NSE-BSE',
  CDS: 'CDS',
  MCX: 'MCX',
};
const GROUP_ORDER: GroupFilter[] = ['ALL', 'CRYPTO_COIN', 'CRYPTO_RWA', 'NSE_BSE', 'CDS', 'MCX'];

/**
 * Which group a cell belongs to. Prefers the API's assetClass; falls back to the
 * exchange so a row from an older cached payload still lands somewhere sensible
 * instead of vanishing from every filter.
 */
const groupOf = (c: HeatmapCell): GroupFilter | 'OTHER' => {
  switch (c.assetClass) {
    case 'CRYPTO': return 'CRYPTO_COIN';
    case 'RWA_EQUITY':
    case 'RWA_METAL': return 'CRYPTO_RWA';
    case 'EQUITY_IN': return 'NSE_BSE';
    case 'CDS': return 'CDS';
    case 'MCX': return 'MCX';
    case undefined: break;
    default: return 'OTHER';
  }
  const e = (c.exchange || '').toUpperCase();
  if (e === 'DELTA') return 'CRYPTO_COIN';
  if (e === 'NSE' || e === 'BSE') return 'NSE_BSE';
  if (e === 'CDS') return 'CDS';
  if (e === 'MCX') return 'MCX';
  return 'OTHER';
};

/** Compact USD turnover: 893K / 1.01B. Full precision lives in the tooltip. */
const fmtUsd = (v?: number | null): string => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

/**
 * Advance/decline sparkline — one series, so no legend; the strip's own text
 * names it. The reading is POLARITY around 1.0 (more advancing than declining),
 * which is a diverging job: two hues with a neutral midpoint. So the area is
 * tinted bull ABOVE the 1.0 baseline and bear BELOW it, the baseline itself is a
 * dashed recessive rule, and the line stays an ink colour rather than taking a
 * series hue — text and line colour never encode the value's rank.
 */
const AdSparkline: React.FC<{
  points: Array<{ t: number; ratio: number; a: number; d: number }>;
  C: any; isDark: boolean;
}> = ({ points, C, isDark }) => {
  const W = 260, H = 40, PAD = 5;
  const [hover, setHover] = useState<number | null>(null);
  if (points.length < 2) return null;

  // Symmetric log scale around 1.0 so 2.0 and 0.5 sit equally far from the
  // baseline. A linear ratio axis squashes every bearish reading into [0,1) and
  // gives half the plot to a case that is just as informative as its mirror.
  const lr = points.map(p => Math.log(Math.max(0.05, Math.min(20, p.ratio))));
  // 25% headroom above the largest reading. Without it the extreme sample pins
  // to the very edge, and on a strongly one-sided session (A/D 10.6, every
  // sample near the max) the result is a flat line welded to the frame — which
  // is TRUE but reads as a broken chart rather than as uniform breadth.
  const span = Math.max(0.35, ...lr.map(v => Math.abs(v))) * 1.25;
  const x = (i: number) => PAD + (i * (W - 2 * PAD)) / (points.length - 1);
  const y = (v: number) => H / 2 - (v / span) * (H / 2 - PAD);
  const mid = H / 2;

  const line = lr.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const areaUp = `${line} L${x(lr.length - 1).toFixed(1)},${mid} L${x(0).toFixed(1)},${mid} Z`;

  const h = hover != null ? points[hover] : null;
  return (
    <Tooltip arrow placement="top"
      title={h ? `${new Date(h.t).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} · A/D ${h.ratio.toFixed(2)} · ${h.a} up / ${h.d} down` : 'Advance/decline ratio over the session'}>
      <Box component="svg" width={W} height={H} viewBox={`0 0 ${W} ${H}`}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e: React.MouseEvent<SVGSVGElement>) => {
          const r = e.currentTarget.getBoundingClientRect();
          const rel = ((e.clientX - r.left) / r.width) * W;
          const i = Math.round(((rel - PAD) / (W - 2 * PAD)) * (points.length - 1));
          setHover(Math.max(0, Math.min(points.length - 1, i)));
        }}
        sx={{
          display: 'block', overflow: 'visible', cursor: 'crosshair',
          // A bare polyline floating in the header did not read as a chart.
          // A recessive frame + surface gives it edges, which is also what makes
          // "the line is near the top" legible as a value rather than a glitch.
          border: '1px solid', borderColor: 'divider', borderRadius: 0.5,
          bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        }}>
        <defs>
          {/* Clip the single area path at the baseline twice — once each side —
              so one path serves both polarities without a second geometry. */}
          <clipPath id="ad-above"><rect x="0" y="0" width={W} height={mid} /></clipPath>
          <clipPath id="ad-below"><rect x="0" y={mid} width={W} height={H - mid} /></clipPath>
        </defs>
        <path d={areaUp} fill={C.bull} opacity={isDark ? 0.28 : 0.20} clipPath="url(#ad-above)" />
        <path d={areaUp} fill={C.bear} opacity={isDark ? 0.28 : 0.20} clipPath="url(#ad-below)" />
        <line x1={0} y1={mid} x2={W} y2={mid} stroke={C.dim} strokeWidth={1} strokeDasharray="3 3" opacity={0.7} />
        <path d={line} fill="none" stroke={C.dim} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {h && (
          <>
            <line x1={x(hover!)} y1={0} x2={x(hover!)} y2={H} stroke={C.dim} strokeWidth={1} opacity={0.5} />
            <circle cx={x(hover!)} cy={y(lr[hover!])} r={3.5} fill={C.dim} />
          </>
        )}
      </Box>
    </Tooltip>
  );
};

const SymbolHeatmap: React.FC<Props> = ({ cells, title = 'Symbol heatmap', subheader, splitTopBottom, breadth }) => {
  const { C, isDark } = useAppTheme();
  const navigate = useNavigate();
  const { openChartTab } = useTabs();
  void navigate; // navigate kept available for future actions
  const [filter, setFilter] = useState<GroupFilter>(() => {
    try { return (localStorage.getItem(FILTER_KEY) as GroupFilter) || 'ALL'; } catch { return 'ALL'; }
  });
  const persistFilter = (v: GroupFilter) => {
    setFilter(v);
    try { localStorage.setItem(FILTER_KEY, v); } catch { /* ignore */ }
  };

  const filteredCells = useMemo(() => {
    if (filter === 'ALL') return cells;
    return cells.filter(c => groupOf(c) === filter);
  }, [cells, filter]);

  const renderCell = (c: HeatmapCell, mode: 'pct' | 'turnover' = 'pct') => {
    const { bg, text } = bucketColor(c.pctChange, C, isDark);
    const pctLabel = c.pctChange == null ? '—' : `${c.pctChange >= 0 ? '+' : ''}${c.pctChange.toFixed(2)}%`;
    // The Most Active row still tints by % — the colour language stays one thing
    // across all three rows — but its second line reports turnover, which is what
    // that row is ranked by. A tile whose label and ranking disagree is unreadable.
    const second = mode === 'turnover' ? fmtUsd(c.turnoverUsd) : pctLabel;
    const tip = [
      c.symbol,
      c.spotPrice != null ? Number(c.spotPrice).toLocaleString('en-IN') : null,
      pctLabel,
      c.turnoverUsd != null ? `24h turnover ${fmtUsd(c.turnoverUsd)}` : null,
    ].filter(Boolean).join(' · ');
    return (
      <Tooltip key={`${mode}:${c.exchange || ''}:${c.symbol}`} arrow title={tip}>
        <Box onClick={() => openChartTab(c.symbol, c.exchange || '')}
          sx={{
            bgcolor: bg, color: text, borderRadius: 0.8, px: 0.4, py: 0.35, cursor: 'pointer',
            border: '1px solid', borderColor: 'divider', minWidth: 0, overflow: 'hidden',
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
            transition: 'transform 0.1s ease', '&:hover': { transform: 'translateY(-1px)' },
          }}>
          <Typography sx={{ fontSize: CH_TYPE.micro, fontWeight: 800, lineHeight: 1.1, width: '100%' }} noWrap>{c.symbol}</Typography>
          <Typography sx={{ fontSize: CH_TYPE.micro, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{second}</Typography>
        </Box>
      </Tooltip>
    );
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = { ALL: cells.length };
    for (const cell of cells) {
      const g = groupOf(cell);
      c[g] = (c[g] || 0) + 1;
    }
    return c;
  }, [cells]);

  /* Advance / decline over the FILTERED universe, with its size, because the
     size is the caveat. This is breadth across what this account tracks, not
     across a market, and the strip says which. */
  const ad = useMemo(() => {
    let a = 0, d = 0, u = 0;
    for (const c of filteredCells) {
      if (c.pctChange == null || !Number.isFinite(c.pctChange)) continue;
      if (c.pctChange > 0) a++; else if (c.pctChange < 0) d++; else u++;
    }
    const n = a + d + u;
    return { a, d, u, n, ratio: d > 0 ? a / d : (a > 0 ? Infinity : 0) };
  }, [filteredCells]);

  /** History for the current group, projected out of the stored samples. */
  const adSeries = useMemo(() => {
    if (!breadth || breadth.length < 2) return [];
    const wanted: string[] = filter === 'ALL'
      ? ['CRYPTO_COIN', 'CRYPTO_RWA', 'NSE_BSE', 'CDS', 'MCX', 'FNO', 'OTHER']
      : [filter];
    const out: Array<{ t: number; ratio: number; a: number; d: number }> = [];
    for (const p of breadth) {
      let a = 0, d = 0;
      for (const g of wanted) { const b = p.groups?.[g]; if (b) { a += b.a; d += b.d; } }
      if (a + d === 0) continue;
      out.push({ t: p.t, ratio: d > 0 ? a / d : a, a, d });
    }
    return out;
  }, [breadth, filter]);

  // NOT "tracked". The Delta side now covers every live perpetual, not just the
  // instruments this user hand-tracked (220 vs 188 on 2026-09-03), so calling
  // the denominator "tracked symbols" would be a number and a word that
  // disagree. "covered" is what it actually is.
  const universeLabel = filter === 'ALL' ? 'covered' : GROUP_LABEL[filter];

  /** Rows ranked three ways. Kept together so the empty-state text can say which
   *  row is empty and why (turnover is Delta-only). */
  const rows = useMemo(() => {
    const valid = filteredCells.filter(c => c.pctChange != null && Number.isFinite(c.pctChange));
    const gainers = valid.filter(c => (c.pctChange as number) > 0).sort((x, y) => (y.pctChange as number) - (x.pctChange as number)).slice(0, 15);
    const losers = valid.filter(c => (c.pctChange as number) < 0).sort((x, y) => (x.pctChange as number) - (y.pctChange as number)).slice(0, 15);
    // Ranked WITHIN the active filter on purpose. Ranked globally, the tokenised
    // metals (PAXG/XAUT turn over ~$768M against ~$1M for the equity tokens)
    // would hold the row permanently and it would never say anything new.
    const active = filteredCells
      .filter(c => Number.isFinite(Number(c.turnoverUsd)) && Number(c.turnoverUsd) > 0)
      .sort((x, y) => Number(y.turnoverUsd) - Number(x.turnoverUsd)).slice(0, 15);
    return { gainers, losers, active };
  }, [filteredCells]);

  const renderRow = (label: string, items: HeatmapCell[], color: string, mode: 'pct' | 'turnover', emptyNote?: string) => (
    <Box>
      <Typography sx={{ fontWeight: 800, fontSize: CH_TYPE.small, letterSpacing: 0.4, color, mb: 0.4, textTransform: 'uppercase' }}>
        {label} ({items.length})
      </Typography>
      {items.length === 0 ? (
        <Typography sx={{ fontSize: CH_TYPE.small, color: 'text.secondary', mb: 0.6 }}>{emptyNote || '—'}</Typography>
      ) : (
        <Box sx={{
          display: 'grid',
          gap: 0.3,
          gridTemplateColumns: {
            xs: 'repeat(auto-fill, minmax(72px, 1fr))',
            sm: 'repeat(auto-fill, minmax(72px, 1fr))',
            md: 'repeat(8, minmax(0, 1fr))',
            lg: 'repeat(15, minmax(0, 1fr))',
          },
        }}>
          {items.map(c => renderCell(c, mode))}
        </Box>
      )}
    </Box>
  );

  return (
    <Card sx={{ borderLeft: `4px solid ${C.bull}`, borderRadius: 1.2 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography fontWeight={700} component="span">{title}</Typography>
            {subheader && (
              <Typography variant="caption" color="text.secondary" component="span" sx={{ fontWeight: 400 }}>
                {subheader}
              </Typography>
            )}
          </Box>
        }
        action={
          <ToggleButtonGroup
            size="small" exclusive
            value={filter}
            onChange={(_, v) => { if (v) persistFilter(v as GroupFilter); }}
            sx={{ '& .MuiToggleButton-root': { px: 1.25, py: 0.25, fontSize: CH_TYPE.small, fontWeight: 700, textTransform: 'none' } }}
          >
            {/* Every group is ALWAYS shown, disabled at zero rather than hidden.
                NSE-BSE / CDS / MCX go to zero whenever the Zerodha session has
                expired — which is most of the time, since its token expires
                daily — and a filter that vanishes reads as a missing feature
                rather than as an empty one. The (0) plus the subheader's reason
                says what actually happened. */}
            {GROUP_ORDER.map(g => (
              <ToggleButton key={g} value={g} disabled={g !== 'ALL' && (counts[g] || 0) === 0}>
                {GROUP_LABEL[g]} ({counts[g] || 0})
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        }
        sx={{ pb: 0.5 }}
      />
      <CardContent>
        {filteredCells.length === 0 ? (
          <EmptyState
            kind="chart"
            size="sm"
            title={cells.length === 0 ? 'No symbols tracked' : `No ${GROUP_LABEL[filter]} symbols available`}
            description={cells.length === 0
              ? "Add instruments to your watchlists and they'll show up here colour-coded by 24h move."
              : `Switch to a different group above, or add ${GROUP_LABEL[filter]} symbols to your watchlists.`}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
            {/* Advance/decline. The denominator is stated because it is the
                caveat: this is breadth over what this account covers, and at
                these counts it is NOT a market reading. Saying so beats a ratio
                that quietly implies one. */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
              justifyContent: 'space-between',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 800, fontSize: CH_TYPE.small, letterSpacing: 0.4, textTransform: 'uppercase', color: 'text.secondary' }}>
                  Advance / decline
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: CH_TYPE.body, fontVariantNumeric: 'tabular-nums' }}>
                  {ad.d > 0 ? (ad.a / ad.d).toFixed(2) : (ad.a > 0 ? '∞' : '—')}
                </Typography>
                <Typography sx={{ fontSize: CH_TYPE.small, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                  {ad.a} up · {ad.d} down{ad.u ? ` · ${ad.u} flat` : ''} of {ad.n} {universeLabel} symbol{ad.n === 1 ? '' : 's'}
                  {' — breadth across the symbols this account covers, not the market'}
                </Typography>
              </Box>
              {adSeries.length >= 2
                ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {/* The sparkline is one series, so it carries no legend — but
                        it does need to say what it is and what its midline means,
                        or a line sitting high in a box is just a line. */}
                    <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.secondary', textAlign: 'right', lineHeight: 1.25 }}>
                      A/D over<br />{adSeries.length} samples
                    </Typography>
                    <AdSparkline points={adSeries} C={C} isDark={isDark} />
                    <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.secondary', lineHeight: 1.25 }}>
                      above<br />= 1.0
                    </Typography>
                  </Box>
                )
                : (
                  <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.secondary' }}>
                    intraday A/D history builds while the app is open
                  </Typography>
                )}
            </Box>

            {renderRow('Top Gainers', rows.gainers, C.bull, 'pct')}
            {renderRow('Top Losers', rows.losers, C.bear, 'pct')}
            {renderRow('Most Active · 24h turnover', rows.active, C.neutral || C.dim, 'turnover',
              'No turnover data for this group — the exchange feed for these symbols reports price only.')}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SymbolHeatmap;
